/**
 * Registry of per-feature "view settings" — toggles that gate internal /
 * expert-mode UI affordances. Replaces the single `expertMode` master switch
 * (PLT-4824). Each entry is a (key, label, group, description) tuple; the key
 * is the stable identifier persisted in localStorage and used by the
 * `useViewSetting(key)` hook.
 *
 * Keys are dot-namespaced so the View settings UI can render them grouped
 * without a separate group lookup, and so future per-key telemetry / migration
 * tooling has a stable wire format.
 *
 * Defaults: a key defaults to `false` unless its definition sets
 * `defaultEnabled: true`. Only a value that differs from the key's default is
 * stored, so changing a default reaches every team user who has not touched
 * that key. The `useViewSetting` hook additionally re-checks team-user status
 * (defence in depth) so a stale localStorage value on a non-team account is a
 * no-op. Mirror of the pattern already established by
 * `useIsChatInternalMode` (PLT-4475).
 */

export type ViewSettingGroupId =
  | 'searchComparison'
  | 'netImpact'
  | 'holdings'
  | 'dma'
  | 'regulatory'
  | 'companyProduct'
  | 'blanket'
  | 'internalAdmin';

export interface ViewSettingGroup {
  id: ViewSettingGroupId;
  label: string;
  description: string;
}

export const VIEW_SETTING_GROUPS: readonly ViewSettingGroup[] = [
  {
    id: 'searchComparison',
    label: 'Search & comparison',
    description: 'Extra entity types and columns in search and comparison surfaces.',
  },
  {
    id: 'netImpact',
    label: 'Net impact lens',
    description: 'Extra controls and detail in the net-impact histograms, time tracking, and impact rows.',
  },
  {
    id: 'holdings',
    label: 'Holdings & constituents',
    description: 'Extra columns and click-through behaviour in holdings and constituents tables.',
  },
  {
    id: 'dma',
    label: 'DMA / CSRD materiality',
    description: 'Internal columns, monetary cells, side-panel extras, and deprecated single-matter views in CSRD DMA.',
  },
  {
    id: 'regulatory',
    label: 'PAI & EU regulatory',
    description: 'Work-in-progress PAI metrics and optional indicator disclosures.',
  },
  {
    id: 'companyProduct',
    label: 'Company / product surfaces',
    description: 'Extra fields, drilldown depth, and admin shortcuts on company and product views.',
  },
  {
    id: 'blanket',
    label: 'Blanket product sets',
    description:
      'Internal "expert" access level for blanket product sets — extra columns, archived items, and editing affordances.',
  },
  {
    id: 'internalAdmin',
    label: 'Internal & admin',
    description: 'Internal-tooling affordances on the platform chrome and admin views.',
  },
] as const;

export interface ViewSettingDefinition {
  key: ViewSettingKey;
  label: string;
  group: ViewSettingGroupId;
  /** One-sentence description shown next to the toggle. */
  description: string;
  /**
   * The value for a team user who has never touched the toggle. Omitted means
   * off. Non-team users get `false` whatever this says (`useViewSetting`).
   */
  defaultEnabled?: true;
}

/**
 * Stable string union of every view-setting key. Adding a new key requires
 * appending to both this union and `VIEW_SETTINGS` below — TypeScript will
 * complain if they go out of sync because `VIEW_SETTINGS` is typed as
 * `readonly ViewSettingDefinition[]` and the registry entries are required to
 * cover every member of this union.
 */
export type ViewSettingKey =
  // search-comparison
  | 'searchComparison.expandedEntityTypes'
  | 'searchComparison.expertFundScreenerFilters'
  | 'searchComparison.devScreenerColumns'
  | 'searchComparison.screenerQueryTimings'
  | 'searchComparison.constituentsInaccessibleRowClickthrough'
  // net-impact
  | 'netImpact.histogramFeatureSelector'
  | 'netImpact.timeTrackingCompareModeButton'
  | 'netImpact.timeSeriesMetaClickDrillthrough'
  | 'netImpact.barAnnotations'
  | 'netImpact.complexInfoModalDevExtras'
  // holdings
  | 'holdings.extraColumns'
  | 'holdings.productSetQualityColumn'
  | 'holdings.publicDataPointsColumn'
  | 'holdings.groupfolioInaccessibleRowsClickthrough'
  // dma
  | 'dma.financialEffectsCells'
  | 'dma.imResultsExtras'
  | 'dma.fmResultsExtras'
  | 'dma.sidePanelImpactExtras'
  | 'dma.deprecatedIroSidepanelExtras'
  | 'dma.deprecatedScoreBasedExtras'
  // regulatory
  | 'regulatory.wipPaiMetrics'
  | 'regulatory.optionalPaiIndicators'
  | 'regulatory.paiValueDisclaimer'
  // company-product
  | 'companyProduct.incomeField'
  | 'companyProduct.resultContextSelector'
  | 'companyProduct.dadaFlagging'
  | 'companyProduct.productDrilldownArticleLimit'
  | 'companyProduct.modelReleaseVariantLink'
  | 'companyProduct.sectionAnchorCopyButtons'
  | 'companyProduct.leena2TopImpactsInDrilldown'
  | 'leena2.showUncertainty'
  // blanket
  | 'blanket.expertAccessLevel'
  | 'blanket.showArchived'
  | 'blanket.ownersColumn'
  | 'blanket.plgSegmentEditor'
  // internal-admin
  | 'internalAdmin.headerVersionSelector'
  | 'internalAdmin.mixinTemplatesArchived'
  | 'internalAdmin.variantModalReleaseNotesVerbose'
  | 'internalAdmin.reactQueryDevtools'
  | 'internalAdmin.showStatusBar'
  | 'internalAdmin.unifiedShell'
  | 'internalAdmin.supplierAllowanceControls';

export const VIEW_SETTINGS: readonly ViewSettingDefinition[] = [
  // -------- Search & comparison --------
  {
    key: 'searchComparison.expandedEntityTypes',
    group: 'searchComparison',
    label: 'Expanded entity types in comparison search',
    description: 'Show internal entity types (e.g. weight sets, product groups) in the comparison search bar.',
  },
  {
    key: 'searchComparison.expertFundScreenerFilters',
    group: 'searchComparison',
    label: 'Expert filters in fund screener',
    description: 'Add internal-only filter controls to the fund screener.',
  },
  {
    key: 'searchComparison.devScreenerColumns',
    group: 'searchComparison',
    label: 'Internal "dev" columns in screener tables',
    description: 'Show development-only columns (e.g. internal IDs, debug flags) in screener column groups.',
  },
  {
    key: 'searchComparison.screenerQueryTimings',
    group: 'searchComparison',
    label: 'Backend query timings on screeners',
    description:
      'Show per-query backend timings (ms) below the controls block on the company and fund screeners — useful for diagnosing slow responses.',
  },
  {
    key: 'searchComparison.constituentsInaccessibleRowClickthrough',
    group: 'searchComparison',
    label: 'Click-through to inaccessible rows in fund constituents screener',
    description: 'Allow opening rows that the user has no access to and skip the gray-out styling.',
  },
  // -------- Net impact lens --------
  {
    key: 'netImpact.histogramFeatureSelector',
    group: 'netImpact',
    label: 'Feature selector on net-impact histograms',
    description:
      'Show the feature dropdown above company / portfolio / group histograms (overrides the "nir" default).',
  },
  {
    key: 'netImpact.timeTrackingCompareModeButton',
    group: 'netImpact',
    label: 'Compare-mode button on time tracking',
    description: 'Add the "compare datapoints" toggle button to the net-impact time-tracking section.',
  },
  {
    key: 'netImpact.timeSeriesMetaClickDrillthrough',
    group: 'netImpact',
    label: 'Cmd/Ctrl-click drilldown on time-series chart',
    description: 'Enable Cmd-click on a single time-series datapoint to drill into the underlying result.',
  },
  {
    key: 'netImpact.barAnnotations',
    group: 'netImpact',
    label: 'Annotations on net-impact bars',
    description: 'Render the internal annotations next to each impact bar on the net-impact chart.',
  },
  {
    key: 'netImpact.complexInfoModalDevExtras',
    group: 'netImpact',
    label: 'Dev extras in complex-info modal',
    description: 'Show the development-only extras inside the single-impact complex-info modal (hidden in production).',
  },
  // -------- Holdings & constituents --------
  {
    key: 'holdings.extraColumns',
    group: 'holdings',
    label: 'Extra columns: result-inherited-from, weighted coverage, last checked',
    description: 'Add internal-only diagnostic columns to portfolio and group holdings tables.',
  },
  {
    key: 'holdings.productSetQualityColumn',
    group: 'holdings',
    label: 'Product-set quality column',
    description: 'Show the product-set-quality indicator column in the group holdings table.',
  },
  {
    key: 'holdings.publicDataPointsColumn',
    group: 'holdings',
    label: 'Public-data-points column',
    description: 'Show the "has public data points" column in the group holdings table.',
  },
  {
    key: 'holdings.groupfolioInaccessibleRowsClickthrough',
    group: 'holdings',
    label: 'Click-through to inaccessible rows in groupfolio tables',
    description:
      'Allow opening inaccessible rows in groupfolio result-items, drilldown, and top-holdings tables; skip the gray-out styling.',
  },
  // -------- DMA / CSRD materiality --------
  {
    key: 'dma.financialEffectsCells',
    group: 'dma',
    label: 'Financial-effects monetary cells in materiality table',
    description: 'Show monetary EUR figures in the materiality results table (instead of the public bucketed cell).',
  },
  {
    key: 'dma.imResultsExtras',
    group: 'dma',
    label: 'Impact-materiality results extras',
    description: 'Show internal columns, sorts, and product-level details on the impact-materiality results pages.',
  },
  {
    key: 'dma.fmResultsExtras',
    group: 'dma',
    label: 'Financial-materiality results extras',
    description: 'Show internal columns and details on the financial-materiality results pages.',
  },
  {
    key: 'dma.sidePanelImpactExtras',
    group: 'dma',
    label: 'Side-panel impact extras (deep-link icon, analyst footer buttons)',
    description:
      'Show the per-product deep-link icon and the analyst-only footer buttons on the DMA side-panel impact view. ' +
      'Does not affect the contributing-products breakdown itself, which is shown to customers regardless.',
  },
  {
    key: 'dma.deprecatedIroSidepanelExtras',
    group: 'dma',
    label: 'IRO sidepanel extras (deprecated single-matter view)',
    description: 'Show internal IRO details in the deprecated pre-Feb-26 IRO side panel.',
  },
  {
    key: 'dma.deprecatedScoreBasedExtras',
    group: 'dma',
    label: 'Score-based materiality page extras (deprecated)',
    description: 'Show internal extras on the deprecated score-based impact / financial materiality pages.',
  },
  // -------- PAI & EU regulatory --------
  {
    key: 'regulatory.wipPaiMetrics',
    group: 'regulatory',
    label: 'Work-in-progress PAI metrics',
    description: 'Include WIP PAI metrics in the company and group PAI lens views.',
  },
  {
    key: 'regulatory.optionalPaiIndicators',
    group: 'regulatory',
    label: 'Optional PAI indicators (Nestlé)',
    description: 'Show the company-specific optional PAI indicators block on the PAI lens.',
  },
  {
    key: 'regulatory.paiValueDisclaimer',
    group: 'regulatory',
    label: 'PAI value disclaimers',
    description: 'Show the internal-only value disclaimers in the PAI indicator table.',
  },
  // -------- Company / product surfaces --------
  {
    key: 'companyProduct.incomeField',
    group: 'companyProduct',
    label: 'Income field on company title section',
    description: 'Show the income field next to revenue / employees on the company title section.',
  },
  {
    key: 'companyProduct.resultContextSelector',
    group: 'companyProduct',
    label: 'Result-context selector on company-group title',
    description: 'Show the weight-set result-context selector on the company-group title bar.',
  },
  {
    key: 'companyProduct.dadaFlagging',
    group: 'companyProduct',
    label: 'Dada flagging on articles list',
    description: 'Default to showing dada flagging controls on the product article list.',
  },
  {
    key: 'companyProduct.productDrilldownArticleLimit',
    group: 'companyProduct',
    label: 'Higher article limits on product drilldown',
    description:
      'Show more articles in the product drilldown right block and top-articles list (200 vs 40), and disable deduplication.',
  },
  {
    key: 'companyProduct.modelReleaseVariantLink',
    group: 'companyProduct',
    label: 'Variant link on model-release text',
    description: 'Render the internal variant link on the model-release text in the company view.',
  },
  {
    key: 'companyProduct.sectionAnchorCopyButtons',
    group: 'companyProduct',
    label: 'Anchor-link copy buttons on outline section titles',
    description: 'Show the "copy section URL" button next to outline section titles.',
  },
  {
    key: 'companyProduct.leena2TopImpactsInDrilldown',
    group: 'companyProduct',
    label: 'Top impacts (Leena-2) in category drill-in panel',
    description:
      'Show the Leena-2-derived leaf "Top impacts" list in the net-impact category drill-in side panel when the Beta preview version is selected (and for Tesla on every version).',
  },
  {
    key: 'leena2.showUncertainty',
    group: 'companyProduct',
    label: 'Show Leena-2 uncertainty',
    description:
      'Show the 80% interval (p10–p90) of Leena-2 values where the model gave one (Leena-2 v5 results only): whiskers on net-impact bars, and intervals in the Leena-2 top impacts, results and debugger views. Generative results have no interval and look the same either way.',
  },
  // -------- Blanket product sets --------
  {
    key: 'blanket.expertAccessLevel',
    group: 'blanket',
    label: 'Expert access level for blanket product sets',
    description:
      'Promote the user to BlanketViewLevel.EXPERT_MODE — unlocks extra sections, columns, edit affordances, and the column-select dropdown.',
  },
  {
    key: 'blanket.showArchived',
    group: 'blanket',
    label: 'Show archived blanket product sets',
    description: 'Show the "show archived" checkbox in my and company blanket-product-set lists.',
  },
  {
    key: 'blanket.ownersColumn',
    group: 'blanket',
    label: 'Owners column in blanket product set list',
    description: 'Show the owners column in the my-blanket-product-sets list table.',
  },
  {
    key: 'blanket.plgSegmentEditor',
    group: 'blanket',
    label: 'Segment editor on PLG "Update products"',
    description:
      'Enable the hierarchical segment / product editor on the DMAv2 "Update products" page (List / Hierarchy view toggle, indented tree, and the explicit Edit / Save / Recompute flow) for your own team account. This is the team preview channel; customers get the editor via the USE_PRODUCT_SEGMENTS privilege, switched on per user group from the privileges admin UI, instead. Off by default, so team accounts keep the flat list view until opted in.',
  },
  // -------- Internal & admin --------
  {
    key: 'internalAdmin.headerVersionSelector',
    group: 'internalAdmin',
    // The key still says `header` while the selector's home in the unified UI is the start
    // menu, and that divergence is deliberate: the key is persisted per user in localStorage,
    // so renaming it would switch everyone who has the selector on back off (PLT-6732).
    label: 'Product data version selector',
    description:
      'Show the product-data version selector: a row in the internal tools menu at the bottom left, and the ' +
      'dropdown in the legacy dark header. Off by default, so a team account runs on the version its release pins ' +
      'until the user asks to choose one. Picking a version by hand clears the selected release.',
  },
  {
    key: 'internalAdmin.mixinTemplatesArchived',
    group: 'internalAdmin',
    label: 'Mixin templates: show archived + edit',
    description: 'Show archived templates and allow editing on the internal mixin-templates view.',
  },
  {
    key: 'internalAdmin.variantModalReleaseNotesVerbose',
    group: 'internalAdmin',
    label: 'Verbose release notes in variant scenarios modal',
    description:
      'Use the short release-note description (verbose mode flips to extended); affects the variant scenarios comparison modal.',
  },
  {
    key: 'internalAdmin.reactQueryDevtools',
    group: 'internalAdmin',
    label: 'TanStack Query devtools button',
    description:
      'Show the floating TanStack Query devtools button in the bottom-left corner. Off by default so it does not overlap the internal-tools start menu.',
  },
  {
    key: 'internalAdmin.showStatusBar',
    group: 'internalAdmin',
    label: 'Show bottom status bar (and internal tools bar)',
    description:
      'Show the team-only bottom status bar at the bottom of the screen, including the internal tools bar above it. Off by default — turn this on to bring back the legacy status bar. The Alt+P internal-tools toggle still works for the row above, but only when the status bar is visible.',
  },
  {
    key: 'internalAdmin.unifiedShell',
    group: 'internalAdmin',
    // The label says "Unified UI" while the key says `unifiedShell`, and that divergence is
    // deliberate: the key is persisted per user in localStorage, so renaming it would switch
    // everyone who has the shell on back off (PLT-6648).
    label: 'Unified UI (investor home)',
    description:
      'Render the investor home routes (/, favourites, custom companies, manage access) in the unified UI — the ' +
      'white top bar and the rail — instead of under the dark header. On by default for team accounts; ' +
      'turn it off to see what a customer organisation that has not been opted in sees. The start menu at the ' +
      'bottom left and the "New interface" row of the account menu carry the same switch. Customers are opted ' +
      'in per user group with the "Use unified UI ' +
      '(investor)" privilege on the privileges admin page; that privilege does not apply to team accounts, for ' +
      'which this setting is the whole gate.',
    defaultEnabled: true,
  },
  {
    key: 'internalAdmin.supplierAllowanceControls',
    group: 'internalAdmin',
    label: 'Supplier allowance controls on the Suppliers page',
    description:
      'Show "Manage" beside "x of y suppliers used", which opens controls to give back the places of a ' +
      'company’s deleted suppliers and to set how many suppliers it may create. Off by default, so the ' +
      'page looks as it does to the customer until you turn this on.',
  },
] as const;

const KEY_SET = new Set<string>(VIEW_SETTINGS.map(s => s.key));

export function isViewSettingKey(value: string): value is ViewSettingKey {
  return KEY_SET.has(value);
}

export function getViewSettingDefinition(key: ViewSettingKey): ViewSettingDefinition {
  const definition = VIEW_SETTINGS.find(s => s.key === key);
  if (!definition) {
    // VIEW_SETTINGS is keyed off the same union, so a missing entry is a
    // programmer error — surface it loudly rather than returning a default
    // that would silently turn the toggle into a no-op.
    throw new Error(`Unknown view-setting key: ${key}`);
  }
  return definition;
}

export function getViewSettingsForGroup(groupId: ViewSettingGroupId): readonly ViewSettingDefinition[] {
  return VIEW_SETTINGS.filter(s => s.group === groupId);
}

/**
 * The persisted view settings: only the keys whose value differs from their
 * default. An absent key means "at its default", which is what lets a changed
 * default reach users who never touched the toggle.
 */
export type ViewSettingOverrides = Partial<Record<ViewSettingKey, boolean>>;

export function getViewSettingDefault(key: ViewSettingKey): boolean {
  return getViewSettingDefinition(key).defaultEnabled === true;
}

export function isViewSettingOn(overrides: ViewSettingOverrides, key: ViewSettingKey): boolean {
  return overrides[key] ?? getViewSettingDefault(key);
}

/** `overrides` with `key` set to `enabled`, dropping the entry when it equals the default. */
export function withViewSetting(
  overrides: ViewSettingOverrides,
  key: ViewSettingKey,
  enabled: boolean,
): ViewSettingOverrides {
  const next: ViewSettingOverrides = { ...overrides };
  if (enabled === getViewSettingDefault(key)) {
    delete next[key];
  } else {
    next[key] = enabled;
  }
  return next;
}

/**
 * The overrides for "exactly these keys on, every other key off". A default-on
 * key left out of `enabledKeys` is stored as an explicit `false`, so a profile
 * such as "All disabled" really turns it off.
 */
export function viewSettingOverridesForEnabledKeys(enabledKeys: readonly ViewSettingKey[]): ViewSettingOverrides {
  const enabled = new Set(enabledKeys);
  let overrides: ViewSettingOverrides = {};
  for (const { key } of VIEW_SETTINGS) {
    overrides = withViewSetting(overrides, key, enabled.has(key));
  }
  return overrides;
}

/**
 * Drop anything in a persisted blob that is not a current key with a boolean
 * value differing from that key's default. A key may have been removed in a
 * newer build, and a value that now equals a changed default carries no
 * information.
 */
export function cleanViewSettingOverrides(raw: Record<string, unknown>): ViewSettingOverrides {
  const cleaned: ViewSettingOverrides = {};
  for (const [key, value] of Object.entries(raw)) {
    if (isViewSettingKey(key) && typeof value === 'boolean' && value !== getViewSettingDefault(key)) {
      cleaned[key] = value;
    }
  }
  return cleaned;
}

/**
 * Hardcoded view-settings profiles (PLT-4824). A profile is just a shortcut
 * for "apply this exact set of toggles" — applying it replaces the entire
 * view-settings blob, so any key not listed in `enabledKeys` is turned off.
 *
 * Profiles intentionally live in code (not user-editable in the UI yet); the
 * intent is that team members PR new profiles when a useful combination
 * emerges. Once a profile lands here it's available to everyone immediately.
 *
 * `enabledKeys` carries one of:
 * - `'all'`   — enable every registered key
 * - `'none'`  — disable every registered key (replaces the old "reset all")
 * - an array of explicit keys — enable only those, disable the rest
 */
export interface ViewSettingProfile {
  id: string;
  label: string;
  description: string;
  enabledKeys: 'all' | 'none' | readonly ViewSettingKey[];
}

export const VIEW_SETTING_PROFILES: readonly ViewSettingProfile[] = [
  {
    id: 'all-enabled',
    label: 'All enabled',
    description: 'Turn every view setting on.',
    enabledKeys: 'all',
  },
  {
    id: 'all-disabled',
    label: 'All disabled',
    description: 'Turn every view setting off, including the ones that are on by default.',
    enabledKeys: 'none',
  },
  {
    id: 'search-expert',
    label: 'All search expert features enabled',
    description: 'Enable every toggle in the Search & comparison group; turn everything else off.',
    enabledKeys: VIEW_SETTINGS.filter(s => s.group === 'searchComparison').map(s => s.key),
  },
] as const;

/**
 * Resolve a profile's `enabledKeys` into the concrete set of keys to enable.
 * Centralised so both the reducer and any future telemetry / preview tooling
 * agree on what "all" / "none" mean.
 */
export function resolveProfileEnabledKeys(profile: ViewSettingProfile): readonly ViewSettingKey[] {
  if (profile.enabledKeys === 'all') {
    return VIEW_SETTINGS.map(s => s.key);
  }
  if (profile.enabledKeys === 'none') {
    return [];
  }
  return profile.enabledKeys;
}
